/**
 * Email Limbo API Client
 *
 * Handles fetching and managing email messages in limbo state.
 * These are emails with confidence scores between 50-69% that require human review.
 */
import { apiGet, apiPost } from "@/lib/api-client";
import type { BulkReviewRequest, BulkReviewResponse, LimboCountResponse, LimboMessage, ReviewRequest } from "@/types/api/email-limbo";

/**
 * Fetch email messages in limbo state
 *
 * @param skip - Number of records to skip (pagination)
 * @param limit - Maximum number of records to return
 * @param accountId - Optional account ID to filter by (if not provided, returns from all accounts)
 * @param keyword - Optional keyword to search in email content
 * @returns List of limbo messages
 */
export async function fetchLimboMessages(skip: number = 0, limit: number = 50, accountId?: string, keyword?: string): Promise<LimboMessage[]> {
    const params: Record<string, string | number> = {
        skip,
        limit,
    };

    if (accountId) {
        params.account_id = accountId;
    }

    if (keyword) {
        params.keyword = keyword;
    }

    return apiGet<LimboMessage[]>("/email/messages/limbo", { params });
}

/**
 * Fetch count of email messages in limbo state
 * Useful for showing badges/notifications
 *
 * @param accountId - Optional account ID to filter by (if not provided, counts from all accounts)
 * @returns Count of limbo messages
 */
export async function fetchLimboCount(accountId?: string): Promise<number> {
    const params: Record<string, string> = {};

    if (accountId) {
        params.account_id = accountId;
    }

    const response = await apiGet<LimboCountResponse>("/email/messages/limbo/count", { params });
    return response.count;
}

/**
 * Review a single limbo message (approve or reject)
 *
 * @param messageId - The UUID of the message to review
 * @param action - "approve" to process the email, "reject" to mark as irrelevant
 * @param containerId - Optional container ID to associate on approval
 * @returns Review response
 */
export async function reviewMessage(messageId: string, action: "approve" | "reject", containerId?: string): Promise<void> {
    const body: ReviewRequest = { 
        action,
        ...(containerId && { container_id: containerId })
    };
    await apiPost(`/email/messages/${messageId}/review`, body);
}

/**
 * Bulk review multiple limbo messages
 *
 * @param messageIds - Array of message UUIDs to review
 * @param action - "approve" to process emails, "reject" to mark as irrelevant
 * @returns Bulk review response with results
 */
export async function bulkReviewMessages(messageIds: string[], action: "approve" | "reject"): Promise<BulkReviewResponse> {
    const body: BulkReviewRequest = {
        message_ids: messageIds,
        action,
    };
    return apiPost<BulkReviewResponse>("/email/messages/bulk-review", body);
}
